import type { TranslationKey } from '@change-corgi/core/i18n';

import type { UploaderMethodProps } from 'src/app/shared/components/imageUpload';

import type { PetitionEditPageState } from './types';

// Helper function to update a form field with a new value and mark it as dirty
function updateFormField(
	state: PetitionEditPageState,
	fieldKey: keyof PetitionEditPageState['formState'],
	updates: Partial<PetitionEditPageState['formState'][typeof fieldKey]>,
): PetitionEditPageState {
	return {
		...state,
		formState: {
			...state.formState,
			[fieldKey]: {
				...state.formState[fieldKey],
				...updates,
				dirty: true,
			},
		},
	};
}

// Helper function to update multiple form fields at once
function updateMultipleFormFields(
	state: PetitionEditPageState,
	updates: Partial<PetitionEditPageState['formState']>,
): PetitionEditPageState {
	return {
		...state,
		formState: {
			...state.formState,
			...updates,
		},
	};
}

export const mutations = {
	setTitle(state: PetitionEditPageState, value: string): PetitionEditPageState {
		return updateFormField(state, 'ask', { value });
	},

	setDescription(state: PetitionEditPageState, data: { value: string; isDirty: boolean }): PetitionEditPageState {
		return updateFormField(state, 'description', {
			value: data.value,
			dirty: data.isDirty,
		});
	},

	setScope(
		state: PetitionEditPageState,
		value: PetitionEditPageState['formState']['geoReach']['value'],
	): PetitionEditPageState {
		return updateMultipleFormFields(state, {
			geoReach: {
				...state.formState.geoReach,
				value,
				dirty: true,
			},
			relevantLocation: {
				...state.formState.relevantLocation,
				value: {
					...state.formState.relevantLocation.value,
					geoReach: value,
				},
				dirty: true,
			},
		});
	},

	setDecisionMakers(
		state: PetitionEditPageState,
		list: PetitionEditPageState['formState']['decisionMakers']['value'],
	): PetitionEditPageState {
		return updateFormField(state, 'decisionMakers', {
			value: [...list],
		});
	},

	setLocation(
		state: PetitionEditPageState,
		value: PetitionEditPageState['formState']['relevantLocation']['value'],
	): PetitionEditPageState {
		return updateFormField(state, 'relevantLocation', {
			value: {
				...state.formState.relevantLocation.value,
				...value,
			},
		});
	},

	addTag(
		state: PetitionEditPageState,
		tag: PetitionEditPageState['formState']['tags']['value'][number],
	): PetitionEditPageState {
		return updateFormField(state, 'tags', {
			value: [...state.formState.tags.value, tag],
		});
	},

	removeMedia(state: PetitionEditPageState): PetitionEditPageState {
		return updateFormField(state, 'media', {
			value: null,
		});
	},

	removeTag(
		state: PetitionEditPageState,
		tag: PetitionEditPageState['formState']['tags']['value'][number],
	): PetitionEditPageState {
		return updateFormField(state, 'tags', {
			value: state.formState.tags.value.filter((formTag) => formTag !== tag),
		});
	},

	setFieldError(
		state: PetitionEditPageState,
		key: keyof PetitionEditPageState['formState'],
		error: TranslationKey | undefined,
	): PetitionEditPageState {
		return updateFormField(state, key, { error });
	},

	resetFieldError(state: PetitionEditPageState, key: keyof PetitionEditPageState['formState']): PetitionEditPageState {
		return updateFormField(state, key, { error: null });
	},

	setUploader(state: PetitionEditPageState, uploader: UploaderMethodProps | null): PetitionEditPageState {
		return { ...state, uploader };
	},
};
